import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import OnboardingLayout from '../../components/OnboardingLayout'
import DualZoneCardInput from '../../components/DualZoneCardInput'
import OnboardingField from '../../components/onboarding/OnboardingField'
import { invalidFieldsFromChecks, scrollToFirstFieldError } from '../../lib/onboarding/fieldValidation'
import { ONBOARDING, onboardingFieldControlClass } from '../../lib/onboarding/formStyles'
import { ONBOARDING_STEP_COUNT } from '../../lib/onboarding'

import type { OnboardingStepProps } from './stepProps'

const Step0 = ({ onNext, onPrevious, initialData, stepIndex }: OnboardingStepProps) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    first_name: (initialData?.first_name as string) || '',
    last_name: (initialData?.last_name as string) || '',
    dob: (initialData?.dob as string) || '',
    sex: (initialData?.sex as string) || '',
    height_cm: (initialData?.height_cm as string) || '175',
    current_weight_kg: (initialData?.current_weight_kg as string) || '75',
  })
  const [error, setError] = useState('')
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set())

  const handleSubmit = () => {
    setError('')

    const checks = {
      first_name: Boolean(formData.first_name.trim()),
      last_name: Boolean(formData.last_name.trim()),
      dob: Boolean(formData.dob),
      sex: Boolean(formData.sex),
      height_cm: Boolean(formData.height_cm),
      current_weight_kg: Boolean(formData.current_weight_kg),
    }
    const invalid = invalidFieldsFromChecks(checks)
    if (invalid.size > 0) {
      setInvalidFields(invalid)
      setError(t('onboarding.identity.fillAll'))
      scrollToFirstFieldError([...invalid])
      return
    }

    setInvalidFields(new Set())
    onNext({
      ...formData,
      height_cm: parseFloat(formData.height_cm),
      current_weight_kg: parseFloat(formData.current_weight_kg),
    })
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
    setInvalidFields((prev) => {
      if (!prev.has(name)) return prev
      const next = new Set(prev)
      next.delete(name)
      return next
    })
  }

  return (
    <OnboardingLayout step={stepIndex} totalSteps={ONBOARDING_STEP_COUNT} onNext={handleSubmit} onPrev={onPrevious}>
      <form className="space-y-6">
        <div>
          <h2 className={ONBOARDING.heading}>{t('onboarding.identity.heading')}</h2>
          <p className={ONBOARDING.subtitle}>{t('onboarding.identity.subtitle')}</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <OnboardingField id="first_name" label={t('onboarding.identity.firstName')} required invalid={invalidFields.has('first_name')}>
            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              className={onboardingFieldControlClass(ONBOARDING.input, invalidFields.has('first_name'))}
              placeholder="Jane"
            />
          </OnboardingField>
          <OnboardingField id="last_name" label={t('onboarding.identity.lastName')} required invalid={invalidFields.has('last_name')}>
            <input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              className={onboardingFieldControlClass(ONBOARDING.input, invalidFields.has('last_name'))}
              placeholder="Doe"
            />
          </OnboardingField>
        </div>

        <OnboardingField id="dob" label={t('onboarding.identity.dob')} required invalid={invalidFields.has('dob')}>
          <input
            type="date"
            name="dob"
            value={formData.dob}
            onChange={handleChange}
            className={onboardingFieldControlClass(ONBOARDING.input, invalidFields.has('dob'))}
          />
        </OnboardingField>

        <OnboardingField id="sex" label={t('onboarding.identity.sex')} required invalid={invalidFields.has('sex')}>
          <select
            name="sex"
            value={formData.sex}
            onChange={handleChange}
            className={onboardingFieldControlClass(ONBOARDING.select, invalidFields.has('sex'))}
          >
            <option value="">{t('onboarding.form.select')}</option>
            <option value="M">{t('onboarding.identity.sexMale')}</option>
            <option value="F">{t('onboarding.identity.sexFemale')}</option>
            <option value="Other">{t('onboarding.identity.sexOther')}</option>
          </select>
        </OnboardingField>

        <div className="grid gap-4 sm:grid-cols-2">
          <OnboardingField id="height_cm" label={t('onboarding.review.height')} required invalid={invalidFields.has('height_cm')}>
            <DualZoneCardInput
              bare
              label=""
              value={Number(formData.height_cm) || 175}
              unit="cm"
              icon="height"
              min={140}
              max={220}
              onChange={(v) => {
                setFormData((prev) => ({ ...prev, height_cm: String(v) }))
                setInvalidFields((prev) => {
                  if (!prev.has('height_cm')) return prev
                  const next = new Set(prev)
                  next.delete('height_cm')
                  return next
                })
              }}
            />
          </OnboardingField>
          <OnboardingField id="current_weight_kg" label={t('onboarding.review.weight')} required invalid={invalidFields.has('current_weight_kg')}>
            <DualZoneCardInput
              bare
              label=""
              value={Number(formData.current_weight_kg) || 75}
              unit="kg"
              icon="monitor_weight"
              min={40}
              max={150}
              step={0.5}
              onChange={(v) => {
                setFormData((prev) => ({ ...prev, current_weight_kg: String(v) }))
                setInvalidFields((prev) => {
                  if (!prev.has('current_weight_kg')) return prev
                  const next = new Set(prev)
                  next.delete('current_weight_kg')
                  return next
                })
              }}
            />
          </OnboardingField>
        </div>

        {error && <div className={ONBOARDING.error}>{error}</div>}
      </form>
    </OnboardingLayout>
  )
}

export default Step0
